import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const SESSION_KEY = "sat_visit_ticked";
const POLL_MS = 30000;

/** Live site-wide visit counter shown in the footer. Ticks once per browser
 * session (guarded via sessionStorage), then polls every 30s so users see
 * the total climb in real time as others land on the site. */
export default function VisitCounter() {
  const [count, setCount] = useState(null);
  const [tickBump, setTickBump] = useState(false);
  const prevRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const seen = sessionStorage.getItem(SESSION_KEY) === "1";
    const init = async () => {
      try {
        const res = seen
          ? await api.get("/visits")
          : await api.post("/visits/tick");
        if (!mounted) return;
        sessionStorage.setItem(SESSION_KEY, "1");
        setCount(res.data?.count ?? 0);
        prevRef.current = res.data?.count ?? 0;
      } catch (_) {}
    };
    init();

    const poll = setInterval(async () => {
      try {
        const res = await api.get("/visits");
        if (!mounted) return;
        const next = res.data?.count ?? 0;
        if (prevRef.current != null && next > prevRef.current) {
          setTickBump(true);
          setTimeout(() => setTickBump(false), 700);
        }
        prevRef.current = next;
        setCount(next);
      } catch (_) {}
    }, POLL_MS);

    return () => { mounted = false; clearInterval(poll); };
  }, []);

  const display = count == null
    ? "------"
    : count.toString().padStart(6, "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return (
    <span
      className="inline-flex items-center gap-2"
      data-testid="visit-counter"
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full transition-colors ${
          tickBump ? "bg-[color:var(--sat-cyan)] animate-pulse" : "bg-[color:var(--sat-cyan)]/60"
        }`}
        aria-hidden
      />
      <span className="text-[color:var(--sat-text-dim)]">VISITS //</span>
      <span
        className={`tabular-nums transition-colors ${tickBump ? "text-[color:var(--sat-cyan)]" : "text-[color:var(--sat-text)]"}`}
        data-testid="visit-counter-value"
      >
        {display}
      </span>
    </span>
  );
}
